<template>
  <section class="preview-section">
    <div class="section-container">
      <!-- Section Header -->
      <div class="section-header">
        <span class="section-badge">{{ badge }}</span>
        <h2 class="section-title">
          {{ title }}
        </h2>
        <p class="section-subtitle">
          {{ subtitle }}
        </p>
      </div>

      <!-- Preview Grid -->
      <div class="preview-grid">
        <div
          v-for="(item, index) in items"
          :key="index"
          :class="['preview-card', { 'preview-card--image': item.image }]"
        >
          <!-- Image mode -->
          <template v-if="item.image">
            <img
              :src="item.image"
              :alt="item.title"
              class="card-bg-image"
              loading="lazy"
            >
            <div class="card-image-overlay">
              <h3>{{ item.title }}</h3>
              <p>{{ item.description }}</p>
              <router-link
                v-if="item.route"
                :to="item.route"
                class="btn-view-more btn-view-more--light"
              >
                View More
                <i class="fas fa-arrow-right" />
              </router-link>
            </div>
          </template>

          <!-- Icon mode -->
          <template v-else>
            <div :class="['card-icon', item.color || 'green']">
              <i :class="item.icon" />
            </div>
            <h3>{{ item.title }}</h3>
            <p>{{ item.description }}</p>
            <router-link
              v-if="item.route"
              :to="item.route"
              class="btn-view-more"
            >
              View More
              <i class="fas fa-arrow-right" />
            </router-link>
          </template>
        </div>
      </div>

      <!-- Section CTA -->
      <div
        v-if="viewMoreRoute"
        class="section-cta"
      >
        <router-link
          :to="viewMoreRoute"
          class="btn-section-view"
        >
          {{ viewMoreText || 'View All' }}
          <i class="fas fa-arrow-right" />
        </router-link>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'PreviewSection',

  props: {
    badge: {
      type: String,
      default: ''
    },
    title: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      default: ''
    },
    items: {
      type: Array,
      required: true
    },
    viewMoreRoute: {
      type: String,
      default: null
    },
    viewMoreText: {
      type: String,
      default: 'View All'
    }
  }
}
</script>

<style scoped>
.preview-section {
  padding: 3rem 1.5rem;
  background: #f8fafc;
}

.section-container {
  max-width: 1400px;
  margin: 0 auto;
}

.section-header {
  text-align: center;
  margin-bottom: 2rem;
}

.section-badge {
  display: inline-block;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(245, 158, 11, 0.1));
  color: #059669;
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.section-title {
  font-size: clamp(1.5rem, 4vw, 2.5rem);
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 0.75rem;
}

.section-subtitle {
  font-size: 1.125rem;
  color: #64748b;
  max-width: 600px;
  margin: 0 auto;
}

.preview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.preview-card {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
}

.preview-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.1);
}

.card-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin-bottom: 1.25rem;
}

.card-icon.green {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(5, 150, 105, 0.15));
  color: #059669;
}

.card-icon.yellow {
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(217, 119, 6, 0.15));
  color: #d97706;
}

.preview-card h3 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.75rem;
}

.preview-card p {
  font-size: 0.9375rem;
  color: #64748b;
  line-height: 1.6;
  flex: 1;
  margin-bottom: 1rem;
}

.btn-view-more {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #059669;
  font-weight: 600;
  font-size: 0.875rem;
  text-decoration: none;
  transition: gap 0.2s;
}

.btn-view-more:hover {
  gap: 0.75rem;
}

.section-cta {
  text-align: center;
  margin-top: 1rem;
}

.btn-section-view {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 2rem;
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  font-weight: 600;
  border-radius: 50px;
  text-decoration: none;
  transition: all 0.3s ease;
}

.btn-section-view:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(16, 185, 129, 0.3);
  gap: 0.75rem;
}

@media (max-width: 768px) {
  .preview-section {
    padding: 3rem 1rem;
  }

  .section-title {
    font-size: 1.5rem;
  }

  .preview-grid {
    grid-template-columns: 1fr;
  }
}

/* Image mode cards */
.preview-card--image {
  position: relative;
  overflow: hidden;
  padding: 0;
  min-height: 220px;
}

.card-bg-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.card-image-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 1.5rem;
  background: linear-gradient(transparent 30%, rgba(0, 0, 0, 0.7));
  color: #fff;
}

.card-image-overlay h3 {
  color: #fff;
  margin-bottom: 0.5rem;
}

.card-image-overlay p {
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 0.75rem;
  flex: 0;
}

.btn-view-more--light {
  color: #fff !important;
}

.btn-view-more--light:hover {
  color: #10b981 !important;
}
</style>
